import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Tooltip, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const OSRM_TRIP_URL = 'https://router.project-osrm.org/trip/v1/driving/';
const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

function decodePolyline(str) {
  let index = 0, lat = 0, lng = 0;
  const coords = [];
  while (index < str.length) {
    let b, shift = 0, result = 0;
    do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);
    shift = 0; result = 0;
    do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);
    coords.push([lat / 1e5, lng / 1e5]);
  }
  return coords;
}

let nextId = 1;

function makeIcon(color, text) {
  return L.divIcon({
    className: '',
    html: `<div style="background:${color};color:#fff;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:13px;border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,.4);">${text}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

const gpsIcon = L.divIcon({
  className: '',
  html: `<div style="background:#f59e0b;color:#fff;border-radius:50%;width:30px;height:30px;display:flex;align-items:center;justify-content:center;font-size:16px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.5);">📡</div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

function ClickHandler({ onAdd }) {
  useMapEvents({ click: (e) => onAdd({ id: nextId++, coords: [e.latlng.lat, e.latlng.lng], label: 'Punto en mapa' }) });
  return null;
}

function FitToPoints({ points }) {
  const map = useMap();
  const prevLen = React.useRef(0);
  useEffect(() => {
    setTimeout(() => map.invalidateSize(), 200);
  }, [map]);
  useEffect(() => {
    if (points.length > 0 && points.length !== prevLen.current) {
      prevLen.current = points.length;
      setTimeout(() => {
        if (points.length === 1) {
          map.setView(points[0].coords, 13);
        } else {
          const bounds = L.latLngBounds(points.map((p) => p.coords));
          map.fitBounds(bounds, { padding: [50, 50] });
        }
      }, 300);
    }
  }, [points, map]);
  return null;
}

function WaypointMarker({ wp, role, number, onDrag, onRemove }) {
  const icon = useMemo(() => {
    if (role === 'origen') return makeIcon('#16a34a', 'O');
    if (role === 'destino') return makeIcon('#dc2626', 'D');
    return makeIcon('#2563eb', number);
  }, [role, number]);
  const roleLabel = role === 'origen' ? '🟢 Origen' : role === 'destino' ? '🔴 Destino' : `${number}.`;
  return (
    <Marker position={wp.coords} icon={icon} draggable
      eventHandlers={{ dragend: (e) => { const { lat, lng } = e.target.getLatLng(); onDrag(wp.id, [lat, lng]); } }}>
      <Tooltip direction="top" offset={[0, -14]} permanent>
        <span style={{ fontSize: 11 }}><b>{roleLabel}</b> {wp.label}</span>
      </Tooltip>
      <Popup>
        <b>{roleLabel} {wp.label}</b><br />
        {wp.coords[0].toFixed(5)}, {wp.coords[1].toFixed(5)}<br />
        <button onClick={() => onRemove(wp.id)} style={{ marginTop: 4, cursor: 'pointer', color: 'red', border: 'none', background: 'none', padding: 0, textDecoration: 'underline' }}>Eliminar</button>
      </Popup>
    </Marker>
  );
}

/**
 * Componente reutilizable del ruteador (sin SiteWrapper).
 * Props opcionales: initialOrigen, initialDestino (strings para geocodificar al montar)
 *                   gpsIds (array de IDs de GPS para mostrar último reporte)
 */
const RuteadorContent = ({ initialOrigen, initialOrigenCoords, initialDestino, gpsIds, movId, height = '100%' }) => {
  const [waypoints, setWaypoints] = useState([]);
  const [originId, setOriginId] = useState(null);
  const [destinationId, setDestinationId] = useState(null);
  const [route, setRoute] = useState(null);
  const [info, setInfo] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [coordText, setCoordText] = useState('');
  const [dragIdx, setDragIdx] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [gpsPositions, setGpsPositions] = useState([]);
  const initialized = React.useRef(false);
  const skipFetch = React.useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    const pts = [];
    if (initialOrigen) pts.push({ id: nextId++, coords: null, label: initialOrigen, role: 'origen' });
    if (initialDestino) pts.push({ id: nextId++, coords: null, label: initialDestino, role: 'destino' });
    if (pts.length > 0) {
      Promise.all(pts.map((p) =>
        fetch(`${NOMINATIM_URL}?q=${encodeURIComponent(p.label)}&format=json&limit=1&countrycodes=mx`)
          .then((r) => r.json())
          .then((data) => data[0] ? { ...p, coords: [parseFloat(data[0].lat), parseFloat(data[0].lon)] } : null)
          .catch(() => null)
      )).then((results) => {
        const valid = results.map((r, i) => {
          if (r && r.coords) return r;
          // Fallback: usar coordenadas del corredor para el origen
          if (pts[i].role === 'origen' && initialOrigenCoords) {
            return { ...pts[i], coords: initialOrigenCoords };
          }
          return null;
        }).filter((r) => r && r.coords);
        if (valid.length > 0) {
          setWaypoints(valid);
          const o = valid.find((v) => v.role === 'origen');
          const d = valid.find((v) => v.role === 'destino');
          if (o) setOriginId(o.id);
          if (d) setDestinationId(d.id);
        }
      });
    }
  }, [initialOrigen, initialOrigenCoords, initialDestino]);

  // Obtener último reporte GPS
  const gpsIdsStr = gpsIds ? gpsIds.join(',') : '';
  useEffect(() => {
    if (!gpsIdsStr) return;
    const ids = gpsIdsStr.split(',');
    const token = sessionStorage.getItem('tok');
    const apiUrl = require('../../config/config').api_raiz;
    console.log('Fetching GPS positions for IDs:', ids, 'URL:', `${apiUrl}gpsUltReporte/`);
    Promise.all(ids.map((id) =>
      fetch(`${apiUrl}gpsUltReporte/${id}`, { headers: { 'Authorization': 'Bearer ' + token } })
        .then((r) => {
          console.log('GPS', id, 'status:', r.status);
          if (!r.ok) return null;
          return r.text().then((t) => { console.log('GPS', id, 'body:', t); return t ? JSON.parse(t) : null; });
        })
        .catch((err) => { console.error('GPS fetch error:', id, err); return null; })
    )).then((results) => {
      console.log('GPS parsed results:', results);
      setGpsPositions(results.filter((r) => {
        if (!r) return false;
        const lat = parseFloat(r.latitud);
        const lng = parseFloat(r.longitud);
        return !isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0);
      }).map((r) => ({ ...r, latitud: parseFloat(r.latitud), longitud: parseFloat(r.longitud) })));
    });
  }, [gpsIdsStr]);

  // Debug: log GPS positions when they change
  useEffect(() => {
    if (gpsPositions.length > 0) {
      console.log('GPS positions to render:', gpsPositions.map(g => ({ nombre: g.nombre, latitud: g.latitud, longitud: g.longitud, lat: g.lat, lng: g.lng })));
    }
  }, [gpsPositions]);

  const orderedPoints = useMemo(() => {
    const origin = waypoints.find((w) => w.id === originId);
    const dest = waypoints.find((w) => w.id === destinationId);
    const mid = waypoints.filter((w) => w.id !== originId && w.id !== destinationId);
    const result = [];
    if (origin) result.push(origin);
    result.push(...mid);
    if (dest) result.push(dest);
    return result;
  }, [waypoints, originId, destinationId]);

  const fetchRouteOSRM = useCallback((pts) => {
    const coords = pts.map((wp) => `${wp.coords[1]},${wp.coords[0]}`).join(';');
    fetch(`https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`)
      .then((r) => r.json())
      .then((data) => {
        if (data.routes && data.routes[0]) {
          setRoute(data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]));
          setInfo({ distance: (data.routes[0].distance / 1000).toFixed(1), duration: Math.round(data.routes[0].duration / 60) });
        }
      })
      .catch(() => { setRoute(null); setInfo(null); });
  }, []);

  const fetchRoute = useCallback((pts, hasOrigin, hasDest) => {
    if (pts.length < 2) { setRoute(null); setInfo(null); return; }
    const coords = pts.map((wp) => `${wp.coords[1]},${wp.coords[0]}`).join(';');
    const useTrip = pts.length > 2;
    const url = useTrip
      ? `https://router.project-osrm.org/trip/v1/driving/${coords}?overview=full&geometries=geojson&source=first&destination=last&roundtrip=false`
      : `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`;

    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        const trip = (data.trips && data.trips[0]) || (data.routes && data.routes[0]);
        if (trip) {
          setRoute(trip.geometry.coordinates.map(([lng, lat]) => [lat, lng]));
          setInfo({ distance: (trip.distance / 1000).toFixed(1), duration: Math.round(trip.duration / 60) });
          if (data.waypoints && useTrip) {
            const order = data.waypoints.map((w) => w.waypoint_index);
            if (!order.every((v, i) => v === i)) {
              skipFetch.current = true;
              setWaypoints(order.map((idx) => pts[idx]));
            }
          }
        }
      })
      .catch(() => { setRoute(null); setInfo(null); });
  }, []);

  useEffect(() => {
    if (skipFetch.current) { skipFetch.current = false; if (loadedFromDb.current) { loadedFromDb.current = false; fetchRoute(orderedPoints, originId !== null, destinationId !== null); } return; }
    fetchRoute(orderedPoints, originId !== null, destinationId !== null);
  }, [orderedPoints, originId, destinationId, fetchRoute]);

  const addPoint = useCallback((wp) => setWaypoints((prev) => [...prev, wp]), []);
  const updatePoint = useCallback((id, coords) => { setWaypoints((prev) => prev.map((w) => w.id === id ? { ...w, coords } : w)); }, []);
  const removePoint = useCallback((id) => { setWaypoints((prev) => prev.filter((w) => w.id !== id)); setOriginId((p) => p === id ? null : p); setDestinationId((p) => p === id ? null : p); }, []);
  const toggleOrigin = useCallback((id) => { setOriginId((p) => p === id ? null : id); setDestinationId((p) => p === id ? null : p); }, []);
  const toggleDestination = useCallback((id) => { setDestinationId((p) => p === id ? null : id); setOriginId((p) => p === id ? null : p); }, []);

  const handleGeocode = (e) => {
    e.preventDefault();
    if (!searchText.trim()) return;
    fetch(`${NOMINATIM_URL}?q=${encodeURIComponent(searchText)}&format=json&limit=5&addressdetails=1&countrycodes=mx`)
      .then((r) => r.json())
      .then((data) => { if (data.length > 0) setSearchResults(data); else { alert('No se encontraron resultados'); setSearchResults([]); } })
      .catch(() => alert('Error en búsqueda'));
  };

  const selectResult = (item) => {
    addPoint({ id: nextId++, coords: [parseFloat(item.lat), parseFloat(item.lon)], label: item.display_name.split(',').slice(0, 3).join(',') });
    setSearchResults([]);
    setSearchText('');
  };

  const handleCoords = (e) => {
    e.preventDefault();
    const parts = coordText.split(',').map((s) => parseFloat(s.trim()));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      addPoint({ id: nextId++, coords: parts, label: coordText.trim() });
      setCoordText('');
    } else { alert('Formato: latitud, longitud'); }
  };

  const clearAll = () => { setWaypoints([]); setOriginId(null); setDestinationId(null); setRoute(null); setInfo(null); };
  const reorder = (from, to) => { setWaypoints((prev) => { const n = [...prev]; const [item] = n.splice(from, 1); n.splice(to, 0, item); return n; }); };

  const saveRoute = () => {
    if (!movId || orderedPoints.length < 2 || !info) return;
    const token = sessionStorage.getItem('tok');
    const apiUrl = require('../../config/config').api_raiz;
    const user = JSON.parse(sessionStorage.getItem('usuario'));
    const body = {
      idMovimiento: movId,
      waypoints: JSON.stringify(orderedPoints.map((w) => ({ coords: w.coords, label: w.label, role: w.id === originId ? 'origen' : w.id === destinationId ? 'destino' : null }))),
      distanciaKm: parseFloat(info.distance),
      duracionMin: info.duration,
      idUsuario: user ? user.id : null,
    };
    fetch(`${apiUrl}rutasMovimiento`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
      body: JSON.stringify(body),
    }).then((r) => { if (r.ok) alert('Ruta guardada'); else alert('Error al guardar'); })
      .catch(() => alert('Error al guardar'));
  };

  // Cargar ruta guardada
  const loadedFromDb = React.useRef(false);
  useEffect(() => {
    if (!movId) return;
    const token = sessionStorage.getItem('tok');
    const apiUrl = require('../../config/config').api_raiz;
    fetch(`${apiUrl}rutasMovimiento/movimiento/${movId}`, { headers: { 'Authorization': 'Bearer ' + token } })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => {
        if (data.length > 0 && waypoints.length === 0) {
          const saved = JSON.parse(data[0].waypoints);
          const pts = saved.map((s) => ({ ...s, id: nextId++ }));
          loadedFromDb.current = true;
          skipFetch.current = true;
          setWaypoints(pts);
          const o = pts.find((p) => p.role === 'origen');
          const d = pts.find((p) => p.role === 'destino');
          if (o) setOriginId(o.id);
          if (d) setDestinationId(d.id);
        }
      })
      .catch(() => {});
  }, [movId]);

  const mapRef = React.useRef(null);

  const panelStyle = { position: 'absolute', top: 10, left: 10, zIndex: 1000, background: 'white', padding: 12, borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.3)', width: 300, maxHeight: '80%', overflowY: 'auto' };
  const inputStyle = { width: '100%', padding: '4px 6px', boxSizing: 'border-box' };
  const btnStyle = { padding: '4px 10px', cursor: 'pointer', marginTop: 4 };
  const roleBtnBase = { cursor: 'pointer', border: '1px solid', borderRadius: 3, padding: '1px 5px', fontSize: 10, marginLeft: 3 };

  return (
    <div style={{ width: '100%', height, position: 'relative' }}>
      <div style={panelStyle}>
        <b>Ruteador</b>
        <form onSubmit={handleGeocode} style={{ marginTop: 8 }}>
          <input style={inputStyle} placeholder="Buscar dirección..." value={searchText} onChange={(e) => { setSearchText(e.target.value); setSearchResults([]); }} />
          <button type="submit" style={btnStyle}>Buscar</button>
        </form>
        {searchResults.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0', fontSize: 12, maxHeight: 120, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 4 }}>
            {searchResults.map((item, i) => (
              <li key={i} onClick={() => selectResult(item)}
                style={{ padding: '5px 8px', cursor: 'pointer', borderBottom: '1px solid #eee', background: i % 2 ? '#f9f9f9' : '#fff' }}
                onMouseEnter={(ev) => ev.target.style.background = '#e0edff'}
                onMouseLeave={(ev) => ev.target.style.background = i % 2 ? '#f9f9f9' : '#fff'}
              >📍 {item.display_name}</li>
            ))}
          </ul>
        )}
        <form onSubmit={handleCoords} style={{ marginTop: 8 }}>
          <input style={inputStyle} placeholder="lat, lng (ej: 19.4326, -99.1332)" value={coordText} onChange={(e) => setCoordText(e.target.value)} />
          <button type="submit" style={btnStyle}>Agregar</button>
        </form>
        <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>Clic en el mapa para agregar puntos</div>
        {waypoints.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <b>Puntos ({waypoints.length})</b>
              <button onClick={clearAll} style={{ ...btnStyle, color: 'red', fontSize: 12 }}>Limpiar</button>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0', fontSize: 12 }}>
              {waypoints.map((wp, i) => {
                const isOrigin = wp.id === originId;
                const isDest = wp.id === destinationId;
                return (
                  <li key={wp.id} draggable
                    onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { if (dragIdx !== null && dragIdx !== i) reorder(dragIdx, i); setDragIdx(null); }}
                    style={{ marginBottom: 3, padding: '4px 6px', cursor: 'grab', borderRadius: 4, background: dragIdx === i ? '#e0edff' : isOrigin ? '#e6f9e6' : isDest ? '#fde8e8' : '#f8f8f8', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      ☰ {isOrigin && '🟢 '}{isDest && '🔴 '}{wp.label}
                    </span>
                    <span style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
                      <button onClick={() => toggleOrigin(wp.id)} style={{ ...roleBtnBase, background: isOrigin ? '#16a34a' : '#fff', color: isOrigin ? '#fff' : '#16a34a', borderColor: '#16a34a' }} title="Origen">O</button>
                      <button onClick={() => toggleDestination(wp.id)} style={{ ...roleBtnBase, background: isDest ? '#dc2626' : '#fff', color: isDest ? '#fff' : '#dc2626', borderColor: '#dc2626' }} title="Destino">D</button>
                      <span onClick={() => removePoint(wp.id)} style={{ color: 'red', cursor: 'pointer', marginLeft: 5 }}>✕</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {info && (
          <div style={{ marginTop: 8, padding: '6px 8px', background: '#f0f7ff', borderRadius: 4, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><b>{info.distance} km</b> — {info.duration} min</span>
            {movId && <button onClick={saveRoute} style={{ padding: '2px 8px', cursor: 'pointer', fontSize: 11, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 3 }}>Guardar</button>}
          </div>
        )}
        {gpsPositions.length > 0 && (
          <div style={{ marginTop: 8, padding: '6px 8px', background: '#fff7e6', borderRadius: 4, fontSize: 12 }}>
            📡 GPS en mapa: {gpsPositions.map((g, i) => (
              <span key={i}>
                {i > 0 && ', '}
                <a href="#" onClick={(e) => { e.preventDefault(); if (mapRef.current) mapRef.current.setView([g.latitud, g.longitud], 15); }}
                  style={{ color: '#d97706', textDecoration: 'underline', cursor: 'pointer' }}>{g.nombre}</a>
              </span>
            ))}
          </div>
        )}
        {gpsIdsStr && gpsPositions.length === 0 && (
          <div style={{ marginTop: 8, padding: '6px 8px', background: '#fee', borderRadius: 4, fontSize: 12, color: '#a00' }}>
            ⚠️ GPS IDs: [{gpsIdsStr}] — sin datos de posición
          </div>
        )}
      </div>
      <MapContainer center={[19.386, -99.101]} zoom={12} style={{ width: '100%', height: '100%' }} ref={mapRef}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
        <ClickHandler onAdd={addPoint} />
        <FitToPoints points={orderedPoints} />
        {orderedPoints.map((wp, i) => {
          const role = wp.id === originId ? 'origen' : wp.id === destinationId ? 'destino' : null;
          const midIdx = orderedPoints.filter((w, j) => j < i && w.id !== originId && w.id !== destinationId).length + 1;
          return <WaypointMarker key={wp.id} wp={wp} role={role} number={midIdx} onDrag={updatePoint} onRemove={removePoint} />;
        })}
        {route && <Polyline positions={route} color="blue" weight={5} opacity={0.7} />}
        {gpsPositions.map((gps) => (
          <Marker key={`gps-${gps.idGps}`} position={[gps.latitud, gps.longitud]} icon={gpsIcon}>
            <Tooltip direction="top" offset={[0, -15]} permanent>
              <span style={{ fontSize: 11 }}>📡 {gps.nombre}</span>
            </Tooltip>
            <Popup>
              <b>📡 GPS: {gps.nombre}</b><br />
              Lat: {gps.latitud.toFixed(5)}, Lng: {gps.longitud.toFixed(5)}<br />
              Batería: {gps.bateria}%<br />
              Último reporte: {gps.utc ? new Date(gps.utc).toLocaleString() : 'N/A'}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default RuteadorContent;
